import classNames from 'classnames'
import {useCallback, useEffect, useRef, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useLocation, useNavigate} from 'react-router-dom'
import Breadcrumb from '../../components/core/breadcrumb'
import ContentLoader from '../../components/core/loader'
import Modal from '../../components/core/modal'
import {PageTitle, ResponsivePageTitle} from '../../components/core/title'
import Footer from '../../components/layouts/footer'
import LogsBarChart from '../../components/pages/logManagement/LogsBarChart'
import LogsList from '../../components/pages/logManagement/LogsList'
import {routes} from '../../routes'
import {getLogGroups, getLogs, setIsFetching} from '../../stores/log-management'
import {setActiveTimeIndex} from '../../stores/monitoring'
import {DateFormats, formatDate, TZ} from '../../utils/date'
import renderSkeletonLoaders from '../../utils/loader'
import FilterFields from './FilterFields'
import PageHeader from './PageHeader'
import FilterLogGroup from './FilterLogGroup'
import EmptyContent from '../resources/EmptyContent'
import {getServices} from '../../stores/resources'
import {getTeams, setSelectedTeamId} from '../../stores/teams'
import {useNamespaceTranslations} from '../../utils/hooks/useNamespacesTranslations'

const LOGS_LIMIT = 100

const LogsDashboard = () => {
    const location = useLocation()
    const text = useNamespaceTranslations('log-management')
    const conditionTypes = useNamespaceTranslations('condition-types')
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const initialFieldPair = {
        field_name: '',
        condition: conditionTypes.log_management_conditions.values.equal,
        value: ''
    }

    const defaultFields = text.default_fields
    const modalIds = text.modal_ids

    const logGroupId = new URLSearchParams(location?.search).get(defaultFields.log_group_id)
    const isDarkMode = useSelector(state => state.sidebar.isDarkMode)
    const services = useSelector((state) => state.resources.services)
    const teams = useSelector(state => state.teams.teams)
    const {isFetching, logGroups} = useSelector(state => state.log_management)

    const [logs, setLogs] = useState({count: '', fields: [], data: []})
    const [offset, setOffset] = useState(0)
    const [isFirstLoad, setIsFirstLoad] = useState(true)
    const [fieldPairs, setFieldPairs] = useState([initialFieldPair])
    const [draftFields, setDraftFields] = useState([initialFieldPair])
    const [logGroupFilters, setLogGroupFilters] = useState({
        service: null,
        log_group: null
    })
    const [draftLogGroupFilters, setDraftLogGroupFilters] = useState(null)
    const [filtersQuery, setFiltersQuery] = useState('')
    const [dateRangeQuery, setDateRangeQuery] = useState('')
    const [activeFiltersModalId, setActiveFiltersModalId] = useState(null)
    const [isLoadMoreEnabled, setIsLoadMoreEnabled] = useState(true)
    const [isLoadMoreButtonLoading, setIsLoadMoreButtonLoading] = useState(false)
    const [searched, setSearched] = useState('')

    const logsContainerRef = useRef(null)

    const filteredFieldsCount = fieldPairs.filter(f => f.field_name && f.value).length
    const logsFields = logs?.fields?.length > 0 ? logs?.fields : Object.keys(defaultFields)

    const buildQueryString = useCallback((base, filters, dateRange, offset = null) => {
        let query = logGroupId ?
            `${base}?${defaultFields.log_group_id}=${logGroupId}` :
            `${base}?`
        if (filters) query += `&${filters}`
        if (dateRange) query += `&${dateRange}`
        if (offset !== null) query += `&offset=${offset}`
        return query
    }, [logGroupId])

    const fetchLogsData = useCallback(async({
        filters = '',
        dateRange = '',
        reset = true,
        isFirstLoad = false
    } = {}) => {
        const query = buildQueryString('', filters, dateRange)

        if (reset) {
            setOffset(0)
            setIsLoadMoreEnabled(true)
        }

        const result = await dispatch(getLogs(query, navigate, true))
        if (!result) return

        setLogs(prev => ({
            count: result.count,
            data: reset ? result.data : [...prev.data, ...result.data],
            fields: isFirstLoad ? result.fields : prev.fields
        }))

        setFiltersQuery(filters)
        setDateRangeQuery(dateRange)
        setIsFirstLoad(false)
        if ((result?.count || 0) < LOGS_LIMIT) {
            setIsLoadMoreEnabled(false)
        }
    }, [dispatch, logGroupId, navigate, buildQueryString])

    const loadMoreLogs = async() => {
        setIsLoadMoreButtonLoading(true)

        const newOffset = offset + LOGS_LIMIT
        const query = buildQueryString('', filtersQuery, dateRangeQuery, newOffset)
        const result = await dispatch(getLogs(query, navigate))
        setOffset(newOffset)
        if (!result) return

        setLogs(prev => ({
            ...prev,
            data: [...prev.data, ...(result.data || [])]
        }))

        if ((result?.count || 0) < LOGS_LIMIT) {
            setIsLoadMoreEnabled(false)
        }

        setIsLoadMoreButtonLoading(false)
    }

    const clearLogGroupFilters = () => {
        const hasActiveFilter = draftLogGroupFilters?.service?.value || activeFiltersModalId
        if (!hasActiveFilter) return

        const memberTeamId = teams?.find(team => team?.is_member)?.team_id
        if (memberTeamId) dispatch(setSelectedTeamId(memberTeamId))

        dispatch(getServices())
        setDraftLogGroupFilters(null)
        setLogGroupFilters({...logGroupFilters, log_group: getLogGroupOptions(logGroups)})

        const params = new URLSearchParams(filtersQuery)
        params.delete(defaultFields.log_group_id)
        const stringParams = params.toString()
        setFiltersQuery(stringParams)
        fetchLogsData({filters: stringParams, dateRange: dateRangeQuery})
    }

    const clearFilters = () => {
        const reset = [initialFieldPair]
        setFieldPairs(reset)
        setDraftFields(reset)
        const params = new URLSearchParams(filtersQuery)
        setFiltersQuery(logGroupId ? '' : `${defaultFields.log_group_id}=${params.get(defaultFields.log_group_id)}`)
        if (filteredFieldsCount > 0) {
            fetchLogsData({dateRange: dateRangeQuery})
        }
    }

    const applyFilters = () => {
        const validFields = draftFields.filter(f => f.field_name && f.value)
        const filters = validFields.map(f => `${f.field_name === defaultFields.message.field_name ?
            defaultFields.message.filter_name : f.field_name}=${f.value}`).join('&')

        setFieldPairs(draftFields)
        const prevFiltersQuery = `${filtersQuery}&${filters}`
        setFiltersQuery(prevFiltersQuery)
        if (validFields.length > 0) {
            setSearched('')
            fetchLogsData({filters: prevFiltersQuery, dateRange: dateRangeQuery})
        }
        setActiveFiltersModalId(null)
    }

    const handleSearch = () => {
        const searchKey = defaultFields.message.filter_name
        const hasMessageLike = filtersQuery.includes(`${searchKey}=`)
        const trimmedSearch = searched?.trim()

        if (!trimmedSearch && hasMessageLike) {
            const params = new URLSearchParams(filtersQuery)
            params.delete(searchKey)
            const updatedFilters = params.toString()
            fetchLogsData({filters: updatedFilters, dateRange: dateRangeQuery, reset: true})
            return
        }

        if (!trimmedSearch) return

        const params = new URLSearchParams(filtersQuery)
        params.set(searchKey, trimmedSearch)
        const updatedFilters = params.toString()

        const filteredFieldPairs = fieldPairs.filter(field => field.field_name !== defaultFields.message.field_name)
        setFieldPairs(filteredFieldPairs)

        fetchLogsData({filters: updatedFilters, dateRange: dateRangeQuery})
    }

    const handleDateRangeChange = ({startDate, endDate}) => {
        const from = formatDate(startDate, DateFormats.ISO_DATETIME, TZ.UTC)
        const to = formatDate(endDate, DateFormats.ISO_DATETIME, TZ.UTC)
        const dateQuery = `from_time=${from}&to_time=${to}`

        setDateRangeQuery(dateQuery)
        fetchLogsData({filters: filtersQuery, dateRange: dateQuery})
    }

    const handleModalClose = () => {
        setActiveFiltersModalId(null)
        setDraftFields([...fieldPairs])
    }

    const getLogGroupOptions = data => data?.map(logGroup => ({
        value: logGroup?.id,
        label: logGroup?.name
    }))

    const handleServiceChange = (value) => {
        setLogGroupFilters({
            ...logGroupFilters,
            service: value
        })
    }

    const handleLogGroupChange = (value) => {
        setFiltersQuery(prev => {
            const params = new URLSearchParams(prev)
            params.set(defaultFields.log_group_id, value?.[0]?.value || '')
            return `${params.toString()}`
        })
        setLogGroupFilters(prev => ({
            ...prev,
            log_group: value
        }))
    }

    const applyLogGroupFilters = () => {
        setDraftLogGroupFilters(logGroupFilters)
        fetchLogsData({filters: filtersQuery, dateRange: dateRangeQuery})
        handleModalClose()
    }

    const fetchLogGroups = async(isFirstLoad = false) => {
        const data = await dispatch(getLogGroups(false, [logGroupFilters.service]))
        handleLogGroupChange(draftLogGroupFilters ?
            draftLogGroupFilters.log_group :
            getLogGroupOptions(data))

        setDraftLogGroupFilters(null)
        if (isFirstLoad) fetchLogsData({filters: filtersQuery, dateRange: dateRangeQuery, isFirstLoad: true})
    }

    useEffect(() => {
        setFiltersQuery('')
        setDateRangeQuery('')
        setFieldPairs([initialFieldPair])
        dispatch(setIsFetching(true))
        setIsFirstLoad(true)
        if (logGroupId) {
            fetchLogsData({isFirstLoad: true})
            dispatch(setActiveTimeIndex(null))
            return
        }
        dispatch(getTeams())
        dispatch(getLogGroups(false))
    }, [location?.search])

    useEffect(() => {
        if (activeFiltersModalId === modalIds.field_filters) {
            setDraftFields([...fieldPairs])
        }
    }, [activeFiltersModalId])

    useEffect(() => {
        if (logGroupId) return
        if (draftLogGroupFilters) {
            setLogGroupFilters(draftLogGroupFilters)
            return
        }

        if (services?.[0]) {
            setLogGroupFilters({
                ...logGroupFilters,
                service: services[0]
            })
        }
    }, [services, location?.search])

    useEffect(() => {
        if (logGroupFilters?.service?.value) {
            fetchLogGroups(isFirstLoad)
        }
    }, [logGroupFilters?.service?.value])

    const isPageLoading = isFetching || isFirstLoad

    return (
        <main className={classNames('main', {
            dark: isDarkMode
        })} id="logs-dashboard">
            {activeFiltersModalId && (
                <Modal
                    isOpen
                    id={activeFiltersModalId === modalIds.field_filters ? 'large-modal' : ''}
                    title={activeFiltersModalId === modalIds.field_filters ? text.filters : text.log_group_filters}
                    closeModal={handleModalClose}
                    contentClassName={activeFiltersModalId === modalIds.field_filters ? 'overflow-auto' : ''}
                >
                    {activeFiltersModalId === modalIds.field_filters ?
                        <>
                            <FilterFields
                                logsFields={logsFields}
                                isDarkMode={isDarkMode}
                                fieldPairs={draftFields}
                                onApply={applyFilters}
                                onClear={clearFilters}
                                setFieldsPairs={setDraftFields}
                            />
                        </> : <FilterLogGroup
                            logGroupOptions={getLogGroupOptions(logGroups)}
                            filters={logGroupFilters}
                            onServiceChange={handleServiceChange}
                            onLogGroupChange={handleLogGroupChange}
                            onApply={applyLogGroupFilters}
                            onClear={clearLogGroupFilters}/>}
                </Modal>
            )}
            <div>
                <ResponsivePageTitle />
                <div className="content-title">
                    <div className="left-side">
                        <div>
                            <Breadcrumb data={[routes.log_groups_list, text.section]} />
                            <PageTitle>
                                <h1 className={classNames('page-title', {
                                    dark: isDarkMode
                                })}>{text.title}</h1>
                            </PageTitle>
                        </div>
                    </div>
                </div>
                <PageHeader
                    isDarkMode={isDarkMode}
                    setActiveFiltersModalId={setActiveFiltersModalId}
                    filteredFieldsCount={filteredFieldsCount}
                    filteredLogGroupsCount={logGroupFilters?.log_group?.length || 0}
                    onClearFilters={clearFilters}
                    setSearched={setSearched}
                    onSearch={handleSearch}
                    onRefresh={fetchLogsData}
                    searched={searched}
                    onResetDate={() => {
                        fetchLogsData({filters: filtersQuery})
                        setDateRangeQuery('')
                        dispatch(setActiveTimeIndex(null))
                    }}
                    isAdditionalFilterEnabled={!logGroupId}
                    callbackFn={handleDateRangeChange}
                    onClearLogsGroupFilter={clearLogGroupFilters}
                    isAdditionalFilterActive={logGroupFilters?.log_group?.length > 0 || logGroupFilters?.service?.value}
                />
                {!isPageLoading && logs?.data?.length === 0 && <EmptyContent message={text.no_data} hideButton />}
                <div
                    ref={logsContainerRef}
                    className={classNames('logs-chart', {
                        dark: isDarkMode,
                        'chart-loader': isPageLoading,
                        'display-none': !isFetching && logs.data.length === 0
                    })}
                >
                    {isPageLoading
                        ? <ContentLoader />
                        : <LogsBarChart
                            logs={logs.data}
                            logsContainerRef={logsContainerRef}
                            isDarkMode={isDarkMode}
                        />
                    }
                </div>
                {isPageLoading
                    ? <div className={classNames('loader-container', {
                        dark: isDarkMode
                    })}>
                        {renderSkeletonLoaders(10)}
                    </div>
                    : logs?.data?.length > 0 && (
                        <LogsList
                            logsData={logs.data}
                            onLoadMore={loadMoreLogs}
                            isDarkMode={isDarkMode}
                            isLoadMoreButtonLoading={isLoadMoreButtonLoading}
                            isLoadMoreEnabled={isLoadMoreEnabled}
                        />
                    )}
            </div>
            <Footer />
        </main>
    )
}

export default LogsDashboard
